import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export type Theme = 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Always use dark theme only
  const [theme] = useState<Theme>('dark');

  // Set dark mode CSS variables (only theme)
  useEffect(() => {
    const root = document.documentElement;
    
    // Dark mode variables - transparent backgrounds to show northern aurora
    root.style.setProperty('--color-bg', 'transparent');
    root.style.setProperty('--color-bg-card', 'transparent'); // Made transparent
    root.style.setProperty('--color-bg-panel', 'transparent');
    root.style.setProperty('--color-text-main', '#F6F8FF');
    root.style.setProperty('--color-text-secondary', '#A0B8C8');
    root.style.setProperty('--color-text-muted', '#4A5A6A');
    root.style.setProperty('--color-border', '#22223A');
    root.style.setProperty('--color-shadow', '0 4px 32px 0 rgba(0, 212, 255, 0.10)');
    root.style.setProperty('--color-shadow-hover', '0 6px 40px 0 rgba(38, 217, 38, 0.18)');
    
    // Update scrollbar for dark mode - transparent like landing page
    root.style.setProperty('--scrollbar-track', 'transparent');
    root.style.setProperty('--scrollbar-thumb', 'transparent');
    
    // Update glass effect for dark mode
    root.style.setProperty('--glass-bg', 'rgba(20, 20, 30, 0.6)');
    root.style.setProperty('--glass-border', '#00D4FF');
  }, []);

  const setTheme = (_newTheme: Theme) => {
    // Theme is always dark, no changes needed
  };

  const toggleTheme = () => {
    // Theme toggle disabled - always dark mode
  };

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
